/**
 * Shelf admin design tokens — the single source for colors, type, radii and shadows.
 * Consumed by tailwind.config.js (utility classes) and theme/antdTheme.js (Ant Design).
 * CommonJS so the Tailwind config (Node) can require it too.
 */
// Brand scale built on the Shelf logo navy: 600 = logo top (#345573), 950 = logo bottom (#031728).
const brand = {
    50: '#f1f5f8',
    100: '#e1e9f0',
    200: '#c4d3e0',
    300: '#9cb4c9',
    400: '#7090ad',
    500: '#4c6f8f',
    600: '#345573',
    700: '#284660',
    800: '#1c364e',
    900: '#10273b',
    950: '#031728'
};

// Light blue for info banners (they used to be grey).
const highlight = {
    bg: '#eaf3fc',
    border: '#cfe1f5',
    text: '#284660'
};

// Neutral scale (slate) for text, borders and surfaces.
const neutral = {
    0: '#ffffff',
    25: '#fbfcfd',
    50: '#f7f8fa',
    100: '#f1f3f6',
    200: '#e5e8ed',
    300: '#d2d7df',
    400: '#9aa3b2',
    500: '#6b7585',
    600: '#4b5565',
    700: '#353d4a',
    800: '#1f2530',
    900: '#111620',
    950: '#0a0d14'
};

// Semantic colors: fg = text/icon, bg = soft tinted background, border = tinted hairline.
const semantic = {
    success: { fg: '#0f8a5f', solid: '#12a06e', bg: '#e9f8f1', border: '#c4ecd9' },
    warning: { fg: '#b4630a', solid: '#e08a12', bg: '#fff5e6', border: '#fbe0b5' },
    danger: { fg: '#c4314b', solid: '#e0435f', bg: '#fdecef', border: '#f8cad3' },
    info: { fg: '#0e7490', solid: '#0891b2', bg: '#e8f7fb', border: '#bfe7f1' },
    violet: { fg: '#6d4bd8', solid: '#7c5cf0', bg: '#f1edff', border: '#ddd3ff' }
};

const font = {
    sans: '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace'
};

/** Shelf logo gradient (official logo SVG, common/icons/ShelfIcon): brand 600 → brand 950. */
const logo = { top: brand[600], bottom: brand[950], gradient: `linear-gradient(180deg, ${brand[600]} 0%, ${brand[950]} 100%)` };

const radius = { sm: 6, md: 8, lg: 12, xl: 16 };

const shadow = {
    xs: '0 1px 2px rgba(16, 24, 40, 0.04)',
    sm: '0 1px 2px rgba(16, 24, 40, 0.05), 0 1px 3px rgba(16, 24, 40, 0.04)',
    md: '0 4px 12px -2px rgba(16, 24, 40, 0.08), 0 2px 4px -2px rgba(16, 24, 40, 0.04)',
    lg: '0 16px 40px -8px rgba(16, 24, 40, 0.16), 0 4px 12px -4px rgba(16, 24, 40, 0.06)',
    focus: '0 0 0 3px rgba(47, 128, 247, 0.18)'
};

// Layout constants shared by the shell.
const layout = { sidebarWidth: 248, sidebarCollapsedWidth: 72, topBarHeight: 60, contentMaxWidth: 1440 };

module.exports = { brand, neutral, highlight, semantic, logo, font, radius, shadow, layout };
